import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCode, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaGithub,
  FaArrowUp,
  FaHeart,
  FaWhatsapp,
  FaTimes
} from 'react-icons/fa';
import { IconType } from 'react-icons';
import './Footer.scss';

const Footer: React.FC = () => {
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  
  const CodeIcon = FaCode;
  const MapIcon = FaMapMarkerAlt;
  const PhoneIcon = FaPhone;
  const EnvelopeIcon = FaEnvelope;
  const HeartIcon = FaHeart;
  const ArrowUpIcon = FaArrowUp;
  const WhatsAppIcon = FaWhatsapp;
  const CloseIcon = FaTimes;

  const quickLinks = [
    { name: 'Who We Are', path: '/who-we-are' },
    { name: 'How We Work', path: '/development-process' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Reviews', path: '/development-reviews' },
    { name: 'Blogs', path: '/blogs' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    'Web Application',
    'Mobile App Development',
    'Custom Web Development',
    'Custom Software Development',
    'UI/UX Design',
    'E-commerce Solutions',
    'Digital Marketing',
    'Cloud Solutions',
    'DevOps Services',
    'Maintenance & Support',
  ];

  const socialLinks: { icon: IconType; url: string; label: string; }[] = [
    { icon: FaFacebookF, url: 'https://www.facebook.com/appdeveloperpro', label: 'Facebook' },
    { icon: FaLinkedinIn, url: 'https://www.linkedin.com/company/app-developer-pro', label: 'LinkedIn' },
    { icon: FaGithub, url: 'https://github.com/appdeveloperpro', label: 'GitHub' },
    { icon: WhatsAppIcon, url: 'https://wa.me/918826360188', label: 'WhatsApp' }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="footer">
      {/* Main Footer Content */}
      <div className="footer__main">
        <div className="footer__container">
          <div className="footer__grid">
            {/* Company Info */}
            <motion.div
              className="footer__section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="footer__logo">
                <motion.div
                  className="footer__logo-wrapper"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src="/images/app-developer-pro-e-logo.png" 
                    alt="App Developer Pro Logo" 
                    className="footer__logo-image"
                  />
                  <motion.div 
                    className="footer__logo-glow"
                    animate={{ 
                      opacity: [0.3, 0.6, 0.3],
                      scale: [1, 1.15, 1]
                    }}
                    transition={{ 
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                </motion.div>
              </div>
              <h3 className="footer__tagline">Tailored Digital Solutions That Power Your Growth</h3>
              <p className="footer__description">
                Powerful web/mobile application & software with digital solutions that drive business growth.
              </p>
              <div className="footer__contact-info">
               
                <div className="footer__contact-item">
                  <MapIcon />
                  <span>827, Kachpura, Kundol, Agra, Uttar Pradesh 283111</span>
                </div>
                <div className="footer__contact-item">
                  <PhoneIcon />
                  <span>+918826360188</span>
                </div>
               
                <div className="footer__contact-item">
                  <EnvelopeIcon />
                  <span>hello@appdeveloperpro.com</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              className="footer__section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="footer__title">Quick Links</h3>
              <ul className="footer__links">
                {quickLinks.map((link) => (
                  <li key={link.name} className="footer__link-item">
                    <Link to={link.path} className="footer__link">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              className="footer__section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="footer__title">Our Services</h3>
              <ul className="footer__services">
                {services.map((service) => (
                  <li key={service} className="footer__service-item">
                    {service}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Google Reviews Widget & Social */}
            <motion.div
              className="footer__section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="footer__title">Our Google Reviews</h3>
              <div className="footer__google-review">
                <div className="footer__google-review-header">
                  <div className="footer__google-logo">
                    <svg viewBox="0 0 48 48" width="32" height="32">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      <path fill="none" d="M0 0h48v48H0z"/>
                    </svg>
                  </div>
                  <div className="footer__google-rating">
                    <div className="footer__google-stars">
                      <span className="footer__star">★</span>
                      <span className="footer__star">★</span>
                      <span className="footer__star">★</span>
                      <span className="footer__star">★</span>
                      <span className="footer__star">★</span>
                    </div>
                    <span className="footer__google-score">5.0</span>
                  </div>
                </div>
                <p className="footer__google-text">
                  Trusted by businesses worldwide. Join our growing community of satisfied clients!
                </p>
                <motion.a
                  href="https://share.google/m1CQyScX859oIsZfT"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer__google-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg viewBox="0 0 48 48" width="20" height="20">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Check Google Reviews
                </motion.a>
              </div>
              <div className="footer__social">
                <span className="footer__social-text">Follow Us:</span>
                <div className="footer__social-links">
                  {socialLinks.map((social) => {
                    const IconComponent = social.icon;
                    return (
                      <motion.a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer__social-link"
                        aria-label={social.label}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IconComponent />
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer__bottom">
        <div className="footer__container">
          <div className="footer__bottom-content">
            <p className="footer__copyright">
              © {new Date().getFullYear()} App Developer Pro. All rights reserved. 
              Made with <HeartIcon className="footer__heart" /> by App Developer Pro.
            </p>
            <div className="footer__bottom-links">
              <Link to="/privacy" className="footer__bottom-link">Privacy Policy</Link>
              <Link to="/terms" className="footer__bottom-link">Terms & Conditions</Link>
              <Link to="/disclaimer" className="footer__bottom-link">Disclaimer</Link>
              <Link to="/sitemap.xml" className="footer__bottom-link">Sitemap</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        className="footer__scroll-top"
        onClick={scrollToTop}
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Scroll to top"
      >
        <ArrowUpIcon />
      </motion.button>

      {/* WhatsApp Live Chat Widget */}
      <div className="footer__whatsapp-widget">
        {/* WhatsApp Chat Popup */}
        <AnimatePresence>
          {isWhatsAppOpen && (
            <motion.div
              className="footer__whatsapp-popup"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="footer__whatsapp-header">
                <div className="footer__whatsapp-header-content">
                  <img 
                    src="/images/app-developer-pro-e-logo.png" 
                    alt="App Developer Pro" 
                    className="footer__whatsapp-avatar"
                  />
                  <div className="footer__whatsapp-info">
                    <h4>App Developer Pro</h4>
                    <p>Typically replies within minutes</p>
                  </div>
                </div>
                <button 
                  className="footer__whatsapp-close"
                  onClick={() => setIsWhatsAppOpen(false)}
                  aria-label="Close WhatsApp chat"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="footer__whatsapp-body">
                <div className="footer__whatsapp-message">
                  <p>Hi there! 👋</p>
                  <p>How can we help you today?</p>
                </div>
              </div>
              <div className="footer__whatsapp-footer">
                <a
                  href="https://wa.me/918826360188?text=Hi%20App%20Developer%20Pro%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer__whatsapp-chat-btn"
                >
                  <WhatsAppIcon />
                  Start Chat on WhatsApp
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* WhatsApp Floating Button */}
        <motion.button
          className="footer__whatsapp-btn"
          onClick={() => setIsWhatsAppOpen(!isWhatsAppOpen)}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Open WhatsApp chat"
        >
          <WhatsAppIcon />
          {!isWhatsAppOpen && (
            <motion.span
              className="footer__whatsapp-badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1 }}
            >
              1
            </motion.span>
          )}
        </motion.button>
      </div>
    </footer>
  );
};

export default Footer;
